import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useSettings } from "@/contexts/SettingsContext";

const fmt = (n) => new Intl.NumberFormat("en-US").format(Math.max(0, Math.floor(n)));

/**
 * Live, animated site-visit counter.
 * - Fetches /api/visits once on mount
 * - Tweens the displayed number from previous → current
 * - Slowly drifts upward client-side every settings.visit_growth_seconds
 *   for a "live" feel — admin can pause via settings.visit_growth_paused
 */
export default function SiteVisitsCounter({ className = "" }) {
  const { settings } = useSettings();
  const [target, setTarget] = useState(0);
  const [display, setDisplay] = useState(0);
  const driftRef = useRef(null);
  const rafRef = useRef(null);

  useEffect(() => {
    let active = true;
    const fetchVisits = () => {
      api.get("/visits").then(({ data }) => {
        if (!active) return;
        const t = data.total || 0;
        setTarget((cur) => Math.max(cur, t));
      }).catch(() => {});
    };
    fetchVisits();
    // Re-sync every 60 s so admin manual bumps are reflected for live visitors
    // without a page refresh. Auto-drift continues between syncs.
    const id = setInterval(fetchVisits, 60_000);
    return () => { active = false; clearInterval(id); };
  }, []);

  // Restart drift whenever pause flag or interval changes
  useEffect(() => {
    if (driftRef.current) clearInterval(driftRef.current);
    if (settings?.visit_growth_paused) return;
    const seconds = Math.max(2, parseInt(settings?.visit_growth_seconds ?? 7, 10) || 7);
    driftRef.current = setInterval(() => {
      setTarget((t) => t + Math.floor(Math.random() * 3) + 1);
    }, seconds * 1000);
    return () => driftRef.current && clearInterval(driftRef.current);
  }, [settings?.visit_growth_paused, settings?.visit_growth_seconds]);

  // Tween display → target
  useEffect(() => {
    let from = display;
    const to = target;
    if (to === from) return;
    const duration = Math.min(1400, Math.max(450, Math.abs(to - from) * 0.4));
    const t0 = performance.now();
    const tick = (now) => {
      const k = Math.min(1, (now - t0) / duration);
      const eased = 1 - Math.pow(1 - k, 3);
      const v = from + (to - from) * eased;
      setDisplay(v);
      if (k < 1) rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => rafRef.current && cancelAnimationFrame(rafRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target]);

  return (
    <span className={className} data-testid="site-visits-counter" aria-live="polite">
      {fmt(display)}+
    </span>
  );
}
